import { FC } from 'react'

import { Alert, Text } from '@factorialco/deprecated-design-system'
import { ExpensesExpensableFilter } from 'generated/resources/datatable.graphql'
import {
  ExpensesApprovedWidgetDocument,
  ExpensesApprovedWidgetQuery,
  ExpensesEntitiesExpensableStatusEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number, currency: string) => string
  forcedCurrency: string
}

interface QueryArgsType {
  from?: string
  to?: string
  employeeIds?: number[]
  groupIds?: number[]
  statuses: ExpensesEntitiesExpensableStatusEnum[]
}

const prepareData = (data: ExpensesApprovedWidgetQuery, prepareDataArgs: PreparedDataArgsType) => {
  const hasMultipleCurrencies =
    (data?.expenses?.expensablesConnection?.aggregate?.terms?.currency?.values?.length || 0) > 1
  const currency =
    data?.expenses?.expensablesConnection?.aggregate?.terms?.currency?.values?.[0] ||
    prepareDataArgs.forcedCurrency
  const totalAmount =
    data?.expenses?.expensablesConnection?.aggregate?.terms?.currency?.sum?.amount?.[0] || 0
  const totalCount = data?.expenses?.expensablesConnection?.aggregate?.terms?.currency?.count || 0

  return {
    countersChartDatas: [
      {
        value: totalAmount,
        formattedValue: prepareDataArgs.amountFormatter(totalAmount, currency),
        label: '',
      },
    ],
    hasMultipleCurrencies,
    totalCount,
  }
}

export const buildExpensesApprovedWidget = ({
  widgetTitle,
  queryArgs,
  prepareDataArgs,
  filters,
}: {
  widgetTitle?: string
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
  filters: ExpensesExpensableFilter
}) => {
  const ExpensesApprovedWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas, hasMultipleCurrencies, totalCount } = data

    if (hasMultipleCurrencies) {
      return (
        <Alert color="note">{i18n.t('expenses_v2.list.insights.empty_case.description')}</Alert>
      )
    }

    return (
      <FinanceWidget
        id="expenses-approved-widget"
        title={widgetTitle}
        type={SupportedCharts.COUNTERS}
        showChart={false}
        aspect="wide"
        countersChartDatas={countersChartDatas.map((chartData) => ({
          ...chartData,
        }))}
      >
        <Text size="200">
          {i18n.t('expenses_v2.list.insights.labels.expenses_count', {
            count: totalCount,
          })}
        </Text>
      </FinanceWidget>
    )
  }

  const filteredFilters = removeNullBetween(filters)

  return buildWidget({
    id: 'expensesApprovedWidget',
    component: ExpensesApprovedWidget,
    document: ExpensesApprovedWidgetDocument,
    prepareData: (data: ExpensesApprovedWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: {
      ...queryArgs,
      filter: {
        ...filteredFilters,
      },
    },
  })
}
